import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';

import {
  useDeactivateInterimEmbryo,
  useInvalidateFreezeQueries,
} from '../../../services/freeze/hooks';
import { SelectionService } from '../utils/selectionService';
import { IncompleteEmbryo } from '../utils/types';

type FreezeDialogsProps = {
  showRulesDialog: boolean;
  showNoEmbryosDialog: boolean;
  showNoTempEmbryosDialog: boolean;
  showDiscardDialog: boolean;
  showPredictionTechDialog: boolean;
  showIncompleteEmbryosDialog: boolean;
  setShowRulesDialog: (show: boolean) => void;
  setShowNoEmbryosDialog: (show: boolean) => void;
  setShowNoTempEmbryosDialog: (show: boolean) => void;
  setShowDiscardDialog: (show: boolean) => void;
  setShowPredictionTechDialog: (show: boolean) => void;
  setShowIncompleteEmbryosDialog: (show: boolean) => void;
  onIncompleteEmbryosAccept: () => void;
  onIncompleteEmbryosReject: () => void;
  incompleteEmbryos: IncompleteEmbryo[];
};

export const FreezeDialogs = ({
  showRulesDialog,
  showNoEmbryosDialog,
  showNoTempEmbryosDialog,
  showDiscardDialog,
  showPredictionTechDialog,
  showIncompleteEmbryosDialog,
  setShowRulesDialog,
  setShowNoEmbryosDialog,
  setShowNoTempEmbryosDialog,
  setShowDiscardDialog,
  setShowPredictionTechDialog,
  setShowIncompleteEmbryosDialog,
  onIncompleteEmbryosAccept,
  onIncompleteEmbryosReject,
  incompleteEmbryos,
}: FreezeDialogsProps) => {
  const { mutateAsync: deactivateIntEmbryo } = useDeactivateInterimEmbryo();
  const { invalidateFertilizationList, invalidateFertAndEmbryos } =
    useInvalidateFreezeQueries();

  const handleDeactivateIntEmbryo = async () => {
    const { tempEmbryoIds } = SelectionService.getSelection();
    await deactivateIntEmbryo({ items: tempEmbryoIds });
    SelectionService.updateSelection('temp', []);
    invalidateFertilizationList();
    invalidateFertAndEmbryos();
    setShowDiscardDialog(false);
  };

  const getIncompleteEmbryosMessage = () => {
    let message =
      'Warning: Some temporary embryos do not have required fields:\n\n';

    incompleteEmbryos.forEach((embryo) => {
      message += `Plate ${embryo.plateNumber}, Embryo ${embryo.embryoName}:\n`;
      message += `Missing fields: ${embryo.missingFields.join(', ')}\n\n`;
    });

    message += '\nDo you want to proceed?';
    return message;
  };

  const incompleteEmbryosFooter = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        className="aria-invalid:border-destructive inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border-transparent bg-blue-500 px-4 py-2 text-base font-medium whitespace-nowrap text-white shadow-xs ring-offset-1 transition-all outline-none hover:bg-blue-500/90 focus-visible:border-blue-500 focus-visible:ring-[3px] focus-visible:ring-blue-500/50 disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-3 aria-invalid:ring-red-500/20 dark:aria-invalid:ring-red-500/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
        onClick={onIncompleteEmbryosReject}
      >
        <i className="pi pi-times mr-1" />
        No
      </button>
      <button
        type="button"
        className="aria-invalid:border-destructive inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border-transparent bg-blue-500 px-4 py-2 text-base font-medium whitespace-nowrap text-white shadow-xs ring-offset-1 transition-all outline-none hover:bg-blue-500/90 focus-visible:border-blue-500 focus-visible:ring-[3px] focus-visible:ring-blue-500/50 disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-3 aria-invalid:ring-red-500/20 dark:aria-invalid:ring-red-500/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
        onClick={onIncompleteEmbryosAccept}
        autoFocus
      >
        <i className="pi pi-check" />
        Yes
      </button>
    </div>
  );

  return (
    <>
      <Dialog
        header="Rules"
        visible={showRulesDialog}
        onHide={() => setShowRulesDialog(false)}
        className="w-[50vw]"
      >
        <ul>
          <li>Embryos will not be created if they don't contain an owner</li>
          <li>
            Embryos that require TT will not be created till they have TT/CZ
            checked
          </li>
          <li>
            Prediction Tech will not be overridden if its already filled in
          </li>
          <li>Slaughterhouse Embryos will always auto generate with Stage 0</li>
          <li>
            Slaughterhouse Grade 2 Embryos will contain an 'X' at the end of the
            Cane
          </li>
          <li>
            All 'Fill Down' checkboxes will only fill down when rows are checked
            and stage/grade are filled
          </li>
        </ul>
      </Dialog>

      <Dialog
        header="No Embryos Selected"
        visible={showNoEmbryosDialog}
        onHide={() => setShowNoEmbryosDialog(false)}
        className="w-[50vw]"
      >
        <p>Please select embryos to freeze before proceeding.</p>
      </Dialog>

      <Dialog
        header="No Temporary Embryos Selected"
        visible={showNoTempEmbryosDialog}
        onHide={() => setShowNoTempEmbryosDialog(false)}
        className="w-[50vw]"
      >
        <p>Please select temporary embryos to freeze before proceeding.</p>
      </Dialog>

      <Dialog
        header="Fill in Prediction Tech"
        visible={showPredictionTechDialog}
        onHide={() => setShowPredictionTechDialog(false)}
        className="w-[50vw]"
      >
        <p>Fill in Prediction Tech first.</p>
      </Dialog>

      <ConfirmDialog
        visible={showDiscardDialog}
        onHide={() => setShowDiscardDialog(false)}
        message="Are you sure you want to discard the selected embryos?"
        header="Confirm"
        icon="pi pi-exclamation-triangle"
        accept={handleDeactivateIntEmbryo}
        reject={() => setShowDiscardDialog(false)}
      />

      <Dialog
        header="Incomplete Embryos"
        visible={showIncompleteEmbryosDialog}
        onHide={() => setShowIncompleteEmbryosDialog(false)}
        className="w-[50vw]"
        footer={incompleteEmbryosFooter}
      >
        <div className="whitespace-pre-line">
          {getIncompleteEmbryosMessage()}
        </div>
      </Dialog>
    </>
  );
};
